import { FormattedMessage } from 'react-intl';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { PoolButton, PoolChip, PoolModal, PoolTextField } from '@synergy/components';
import { makeStyles } from '@mui/styles';
import { unique } from '@synergy/utils';

const useStyles = makeStyles(() => ({
    actionChildren: {
        minWidth: 100,
    },
    inputContainer: {
        position: 'sticky',
        top: 0,
        paddingTop: 8,
        paddingBottom: 16,
        marginLeft: -88,
        paddingLeft: 88,
        marginRight: -88,
        paddingRight: 88,
        boxShadow: '0px 2px 14px rgba(0, 0, 0, 0.26)',
        zIndex: 1,
        backgroundColor: '#fff',
    },
    chipArea: {
        width: 424,
        minHeight: 265,
        marginTop: '1.5rem',
    },
    chip: {
        marginLeft: 0,
        marginRight: 8,
    },
}));

interface MappingEditModalProps {
    visible: boolean;
    mapping: string[];
    onSubmit: (mapping: string[]) => void;
    onClose: () => void;
}

export const MappingEditModal = (props: MappingEditModalProps) => {
    const classes = useStyles();
    const [inputValue, setInputValue] = useState<string>('');
    const [mapping, setMapping] = useState<string[]>(props.mapping ?? []);
    const chipAreaBottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (chipAreaBottomRef.current) {
            chipAreaBottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [mapping]);

    const handleEnter = (event: KeyboardEvent) => {
        if (inputValue && event.key === 'Enter') {
            setMapping(
                unique([
                    ...mapping,
                    ...inputValue
                        .split(';')
                        .map(splitValue => splitValue.trim())
                        .filter(splitValue => !!splitValue),
                ]).sort()
            );
            setInputValue('');
        }
    };

    const handleDelete = (index: number) => {
        const nextMapping = [...mapping];
        nextMapping.splice(index, 1);
        setMapping(nextMapping);
    };

    const actionChildren = (
        <>
            <PoolButton
                key="1"
                styleType="firstLevel"
                variant="text"
                onClick={props.onClose}
                className={classes.actionChildren}
            >
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>
            <PoolButton
                key="2"
                styleType="firstLevel"
                onClick={() => props.onSubmit(mapping)}
                className={classes.actionChildren}
            >
                <FormattedMessage id={'app.common.save'} />
            </PoolButton>
        </>
    );

    return (
        <PoolModal
            title={<FormattedMessage id={'app.mapping.edit'} />}
            isVisible={props.visible}
            handleClose={props.onClose}
            actionChildren={actionChildren}
            disableTitleDivider={true}
            sx={{ content: { pt: 0, overflowX: 'hidden' } }}
        >
            {/* TODO: should be replaced with new ChipAutocomplete Component */}
            <div className={classes.inputContainer}>
                <PoolTextField
                    fullWidth={true}
                    label={<FormattedMessage id="app.mapping.placeholder" />}
                    value={inputValue}
                    onChange={event => setInputValue(event.currentTarget.value)}
                    onKeyDown={handleEnter}
                    autoFocus={true}
                />
            </div>
            <div className={classes.chipArea}>
                {mapping.map((value: string, index: number) => (
                    <PoolChip
                        className={classes.chip}
                        key={index}
                        label={value}
                        type="tag"
                        color="primary"
                        onDelete={() => handleDelete(index)}
                    />
                ))}
                <div ref={chipAreaBottomRef} />
            </div>
        </PoolModal>
    );
};
